// src/components/ui/StageUpgradeModal.tsx - ИСПРАВЛЕНО: Реальное конфетти

import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { celebrateStageUpgrade } from '../../utils/confetti'; // ✅ ДОБАВЛЕНО

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(20, 16, 40, 0.8);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
`;

const ModalCard = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 360px;
  border-radius: 32px;
  padding: 40px 28px 32px;
  background: var(--modal-bg);
  box-shadow:
    0 40px 80px rgba(123, 104, 238, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: hidden;
  text-align: center;

  [data-theme="dark"] & {
    box-shadow:
      0 40px 80px rgba(0, 0, 0, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
`;

const GradientBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #22C55E, #10B981, #34D399);
  background-size: 200% 100%;
  animation: shimmer 2s ease-in-out infinite;

  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const IconCircle = styled(motion.div)`
  width: 100px;
  height: 100px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22C55E, #10B981);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 50px;
  box-shadow: 
    0 20px 40px rgba(34, 197, 94, 0.4),
    inset 0 2px 0 rgba(255, 255, 255, 0.3);
`;

const Title = styled(motion.h2)`
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 12px 0;
  background: linear-gradient(135deg, #22C55E, #10B981);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

const Subtitle = styled(motion.p)`
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const StageInfo = styled(motion.div)`
  background: rgba(34, 197, 94, 0.1);
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 28px;
`;

const StageLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #10B981;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const StageTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #22C55E;
  margin-bottom: 8px;
`;

const StageDescription = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 18px 28px;
  border-radius: 20px;
  border: none;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 12px 28px rgba(123, 104, 238, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(123, 104, 238, 0.45);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #9d8eee, #b896dc);
    box-shadow: 0 12px 28px rgba(157, 142, 238, 0.45);

    &:hover {
      box-shadow: 0 16px 36px rgba(157, 142, 238, 0.55);
    }
  }
`;

interface StageUpgradeModalProps {
  stage: 1 | 2 | 3;
  onClose: () => void;
}

const STAGE_INFO = {
  1: {
    title: 'Этап 1: Адаптация',
    description: 'Начальный этап знакомства кожи с ретинолом. Применение 1-2 раза в неделю.'
  },
  2: {
    title: 'Этап 2: Прогресс 🌟',
    description: 'Кожа адаптировалась! Можно увеличить частоту применения до 3 раз в неделю.'
  },
  3: {
    title: 'Этап 3: Мастерство ✨',
    description: 'Высший уровень! Регулярное применение с максимальным эффектом.'
  }
};

export const StageUpgradeModal: React.FC<StageUpgradeModalProps> = ({ stage, onClose }) => {
  const { hapticFeedback } = useTelegramWebApp();
  
  React.useEffect(() => {
    // ✅ ИСПРАВЛЕНО: Реальное конфетти через canvas-confetti (3 сек с боков)
    celebrateStageUpgrade();
    
    // ✅ Вибрация/хаптик
    hapticFeedback('success');
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100, 50, 200]);
    }
  },[hapticFeedback]);

  const stageInfo = STAGE_INFO[stage];

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <ModalCard
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ 
            type: 'spring', 
            stiffness: 300, 
            damping: 25 
          }}
        >
          <GradientBar />

          <IconCircle
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: 'spring', 
              stiffness: 400, 
              damping: 20,
              delay: 0.2
            }}
          >
            🎉
          </IconCircle>

          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Поздравляем!
          </Title>

          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Вы перешли на новый этап применения ретинола
          </Subtitle>

          <StageInfo
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <StageLabel>Новый этап</StageLabel>
            <StageTitle>{stageInfo.title}</StageTitle>
            <StageDescription>{stageInfo.description}</StageDescription>
          </StageInfo>

          <Button
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Продолжить 🚀
          </Button>
        </ModalCard>
      </Overlay>
    </AnimatePresence>
  );
};